import { useLocation } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { track } from "~/lib/analytics";

interface PageViewTrackingOptions {
  readonly country?: string;
  /** Off for a page whose URL is a credential: `page_location` is the URL. */
  readonly enabled?: boolean;
  readonly locale: string;
}

/** Give up waiting for the title to settle after this long and send anyway. */
const TITLE_SETTLE_TIMEOUT_MS = 1500;

/**
 * Fire a GA4 `page_view` on every client-side route change. TanStack Start
 * navigates without a full reload, so GA4 would otherwise only record the first
 * page of a session. gtag's own `config` call already counts that first page,
 * so the initial mount is skipped here to avoid double-counting. Consent-safe:
 * `track` no-ops during SSR and until the consent manager loads gtag.
 *
 * The browser URL updates synchronously on navigation, but `document.title` is
 * applied by `HeadContent` only after the route's head resolves — often many
 * frames later. So we poll until the title changes from the leaving page's
 * title (or a timeout elapses) before sending, ensuring the event carries the
 * new page's title, not the previous one's.
 */
export function usePageViewTracking({
  locale,
  country,
  enabled = true,
}: PageViewTrackingOptions): void {
  const location = useLocation();
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (typeof window === "undefined" || !enabled) {
      return;
    }

    const previousTitle = document.title;
    const startedAt = performance.now();
    let cancelled = false;

    const send = () => {
      track("page_view", {
        country,
        locale,
        page_location: window.location.href,
        page_path: location.pathname,
        page_title: document.title,
      });
    };

    const poll = () => {
      if (cancelled) {
        return;
      }
      const titleSettled = document.title !== previousTitle;
      const timedOut = performance.now() - startedAt > TITLE_SETTLE_TIMEOUT_MS;
      if (titleSettled || timedOut) {
        send();
        return;
      }
      requestAnimationFrame(poll);
    };
    requestAnimationFrame(poll);

    return () => {
      cancelled = true;
    };
  }, [location.pathname, locale, country, enabled]);
}
